/* eslint-disable no-restricted-imports */
import {elementClickEventTypes} from '@dazn/lp-web/src/analytics/fe-events/elementClick';
import {
    triggerElementClick,
    triggerViewItemList
} from '@dazn/lp-web/src/analytics/fe-events/index';
import ComingUpTile from '@dazn/lp-web/src/components/ComingUpRail/ComingUpTile';
import IconChevron from '@dazn/lp-web/src/components/Rail/Chevron';
import styles from '@dazn/lp-web/src/components/Rail/rail.module.css';
import SubscriptionsTile from '@dazn/lp-web/src/components/SubscriptionsRail/SubscriptionsTile';
import {ComponentTypes} from '@dazn/lp-web/src/constants/componentTypes.constants';
import {useMedia} from '@dazn/lp-web/src/hooks/useMedia';
import {useTitleTransitions} from '@dazn/lp-web/src/hooks/useTransitions';
import {
    IArticleTile,
    IComingUpTile,
    ILPRail,
    ILPTile,
    ISubscriptionTile,
    ITile
} from '@dazn/lp-web/src/types';
import {
    getRailSlidesPerView,
    railSliderConfig
} from '@dazn/lp-web/src/utils/railConfig';
import {animated} from '@react-spring/web';
import classnames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import Tile from './Tile';
import isQueryParamTrue from '../../utils/isQueryparamTrue';
import ArticlesTile from '@dazn/lp-web/src/components/ArticlesRail/ArticlesTile';

interface IRailProps {
    rail: ILPRail;
    disableAnimation: boolean;
    isBackgroundImageAvailable?: boolean;
}

const Rail: React.FC<IRailProps> = ({
    rail,
    disableAnimation,
    isBackgroundImageAvailable = false
}) => {
    const swiperRef = useRef<any>();
    const [refView, springs] = useTitleTransitions();
    const [refButton, buttonSprings] = useTitleTransitions();
    const [showPrev, setShowPrev] = useState<boolean>(false);
    const [showNext, setShowNext] = useState<boolean>(false);
    const {isDesktop, isMobile, isTab} = useMedia();
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const [isAtEnd, setIsAtEnd] = useState<boolean>(false);
    const [isAtStart, setIsAtStart] = useState<boolean>(true);

    const isPrimaryButton = rail?.button?.type === 'Primary';

    useEffect(() => {
        if (swiperRef.current) {
            setShowPrev(isDesktop && swiperRef.current.allowSlidePrev);
            setShowNext(isDesktop && swiperRef.current.allowSlideNext);
        }
    }, [swiperRef.current]);

    useEffect(() => {
        if (rail?.tiles && rail?.tiles?.length > 0) {
            const tileImpressions = rail?.tiles?.map((tile, index) => ({
                tileTitle: tile.title || '',
                assetId: tile.assetId || '',
                assetType: 'fixture',
                tilePositionOnLoad: index + 1,
                tilePositionOnView: index + 1,
                numberOfTilesInRail: rail?.tiles.length,
                entitlementId:
                    Array.isArray(tile.entitlementIds) &&
                    tile.entitlementIds?.length > 0
                        ? tile.entitlementIds[0]
                        : '',
                entitlementIds: tile.EntitlementIds,
                railTitle: '',
                railId: '',
                railPosition: '',
                competitionId: tile.competitionId || '',
                competitionName: tile.competitionName || ''
            }));
            if (
                rail?.componentType !== ComponentTypes.SUBSCRIPTIONS_RAIL &&
                rail?.componentType !== ComponentTypes.COMING_UP_RAIL
            ) {
                triggerViewItemList({
                    tileImpressions,
                    scheduleDate: '',
                    scheduleFilter: '',
                    impressionName: 'rail impression',
                    type: 'rail'
                });
            }
        }
    }, [rail?.tiles, rail?.componentType]);

    const handlePrev = () => {
        swiperRef.current?.slidePrev();
        setActiveIndex(swiperRef.current?.activeIndex ?? 0);
    };

    const handleNext = () => {
        swiperRef.current?.slideNext();
        setActiveIndex(swiperRef.current?.activeIndex ?? 0);
    };

    useEffect(() => {
        if (swiperRef.current) {
            setIsAtEnd(swiperRef.current.isEnd);
            setIsAtStart(swiperRef.current.isBeginning);
        }
    }, [activeIndex]);

    const isSpotlightTile =
        rail?.componentType === ComponentTypes.SPOTLIGHT_RAIL;

    const slidesPerView = getRailSlidesPerView(window.innerWidth);

    const animations = disableAnimation ? {} : {style: springs, ref: refView};
    const buttonAnimations = disableAnimation
        ? {}
        : {style: buttonSprings, ref: refButton};

    if (!rail?.tiles?.length) {
        return null;
    }

    const showSeeMore = rail.tiles.length >= slidesPerView;
    const centerRailDetails = (() => {
        if (rail.componentType === ComponentTypes.STANDARD_RAIL) {
            return false;
        }

        if (rail.componentType === ComponentTypes.ARTICLES_RAIL && !rail.showGradient) {
            return true;
        }

        return rail.tiles.length < slidesPerView;
    })();

    const handleSeeMoreButton = () => {
        triggerElementClick({
            section: elementClickEventTypes.BODY,
            itemId: 'see_more',
            resourceString: rail?.button?.label || ''
        });
        if (rail?.button?.action) {
            rail?.button?.action();
        }
    };

    const handleMobileSeeMoreButton = () => {
        triggerElementClick({
            section: elementClickEventTypes.BODY,
            itemId: 'see_more',
            resourceString:
                rail?.button?.mobileButtonLabel || rail?.button?.label || ''
        });
        if (rail?.button?.action) {
            rail?.button?.action();
        }
    };

    const isDarkMode = rail?.theme === 'dark';
    return (
        <div
            className={classnames(styles.railWrapper, {
                [styles.railSpotlightWrapper]:
                    rail.componentType === ComponentTypes.SPOTLIGHT_RAIL,
                [styles.railComingUpWrapper]:
                    rail.componentType === ComponentTypes.COMING_UP_RAIL,
                [styles.railSubscriptionsWrapper]:
                    rail.componentType === ComponentTypes.SUBSCRIPTIONS_RAIL,
                [styles.railArticlesWrapper]:
                    rail.componentType === ComponentTypes.ARTICLES_RAIL,
                [styles.railArticlesWrapperWithGradient]:
                    rail.componentType === ComponentTypes.ARTICLES_RAIL && rail.showGradient,  
                [styles.railSpotlightWrapperNoBgImage]:
                    rail.componentType === ComponentTypes.SPOTLIGHT_RAIL &&
                    !isBackgroundImageAvailable,
                [styles.railDarkMode]: isDarkMode
            })}
        >
            <div className={styles.articleRailBackgroundGradient} />
            <animated.div {...animations}>
                <div
                    className={classnames(styles.railHeader, {
                        [styles.isSpotlightRailHeader]: isSpotlightTile,
                        [styles.isDarkModeRailHeader]: isDarkMode
                    })}
                >
                    {!isSpotlightTile && (
                        <div
                            className={classnames(styles.railTitleSection, {
                                [styles.centerRailDetails]:
                                    centerRailDetails && isDesktop,
                                [styles.railTitleSectionDarkMode]: isDarkMode
                            })}
                        >
                            <h1
                                className={classnames(styles.railTitle, {
                                    [styles.isDarkRailTitle]: isDarkMode
                                })}
                            >
                                {rail.title}
                            </h1>
                            {rail?.description && (
                                <p className={styles.railSubTitle}>
                                    {rail?.description}
                                </p>
                            )}
                        </div>
                    )}
                    <div
                        className={classnames(styles.railActions, {
                            [styles.darkModeRailActions]: isDarkMode
                        })}
                    >
                        {rail?.button?.label &&
                            (isDesktop || isTab) &&
                            showSeeMore &&
                            (isQueryParamTrue('seo') ? (
                                <a
                                    href={rail?.button?.navigationLink}
                                    className={classnames('seoButton', {
                                        [styles.seeMore]: isPrimaryButton,
                                        [styles.seeMoreSecondary]:
                                            !isPrimaryButton
                                    })}
                                >
                                    {rail?.button?.label}
                                </a>
                            ) : (
                                <button
                                    className={
                                        isPrimaryButton
                                            ? styles.seeMore
                                            : styles.seeMoreSecondary
                                    }
                                    onClick={handleSeeMoreButton}
                                >
                                    {rail?.button?.label}
                                </button>
                            ))}
                        {showPrev && (
                            <div
                                className={classnames({
                                    [styles.prev]: isPrimaryButton,
                                    [styles.prevSecondary]: !isPrimaryButton,
                                    [styles.disable]: isAtStart
                                })}
                                onClick={handlePrev}
                            >
                                <IconChevron />
                            </div>
                        )}
                        {showNext && (
                            <div
                                className={classnames({
                                    [styles.next]: isPrimaryButton,
                                    [styles.nextSecondary]: !isPrimaryButton,
                                    [styles.disable]: isAtEnd
                                })}
                                onClick={handleNext}
                            >
                                <IconChevron />
                            </div>
                        )}
                    </div>
                </div>
            </animated.div>
            {isDesktop && (
                <Swiper
                    direction="horizontal"
                    speed={500}
                    spaceBetween={20}
                    onSwiper={(swiper: any) => {
                        swiperRef.current = swiper;
                    }}
                    breakpoints={railSliderConfig}
                    centerInsufficientSlides={centerRailDetails}
                >
                    {rail?.tiles?.map((tile: ILPTile, index: number) => {
                        return (
                            <SwiperSlide key={index}>
                                {renderTile(
                                    rail.componentType,
                                    tile,
                                    index,
                                    slidesPerView,
                                    rail?.tiles?.length || 0,
                                    rail,
                                    disableAnimation
                                )}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )}
            {(isMobile || isTab) && (
                <>
                    <div className={classnames(styles.railSlider)}>
                        {rail?.tiles?.map((tile: ILPTile, index: number) =>
                            renderTile(
                                rail.componentType,
                                tile,
                                index,
                                slidesPerView,
                                rail?.tiles?.length || 0,
                                rail,
                                disableAnimation
                            )
                        )}
                    </div>
                    {isMobile && (
                        <animated.div
                            {...buttonAnimations}
                            className={classnames(styles.railActions)}
                        >
                            {(rail?.button?.mobileButtonLabel ||
                                rail?.button?.label) &&
                                (isQueryParamTrue('seo') ? (
                                    <a
                                        href={
                                            rail?.button?.navigationLink ?? ''
                                        }
                                        className={styles.exploreButton}
                                    >
                                        {rail?.button?.mobileButtonLabel ||
                                            rail?.button?.label}
                                    </a>
                                ) : (
                                    <button
                                        className={styles.exploreButton}
                                        onClick={handleMobileSeeMoreButton}
                                    >
                                        {rail?.button?.mobileButtonLabel ||
                                            rail?.button?.label}
                                    </button>
                                ))}
                        </animated.div>
                    )}
                </>
            )}
        </div>
    );
};

const renderTile = (
    componentType: string,
    tile: ILPTile,
    index: number,
    slidesPerView: number,
    tiles: number,
    rail: ILPRail,
    disableAnimation: boolean = false,
) => {
    switch (componentType) {
        case ComponentTypes.COMING_UP_RAIL:
            return (
                <ComingUpTile
                    tile={tile as IComingUpTile}
                    index={index}
                    slidesPerView={slidesPerView}
                    key={index}
                    disableAnimation={disableAnimation}
                />
            );
        case ComponentTypes.SUBSCRIPTIONS_RAIL:
            return (
                <SubscriptionsTile
                    tile={tile as ISubscriptionTile}
                    index={index}
                    slidesPerView={slidesPerView}
                    key={index}
                    disableAnimation={disableAnimation}
                />
            );
        case ComponentTypes.ARTICLES_RAIL:
            return (
                <ArticlesTile
                    tileBackgroundColor={rail?.tileBackgroundColor ?? 'default'}
                    showGradient={rail?.showGradient}
                    tile={tile as IArticleTile}
                    index={index}
                    slidesPerView={slidesPerView}
                    key={index}
                    disableAnimation={disableAnimation}
                />
            );
        case ComponentTypes.SPOTLIGHT_RAIL:
        case ComponentTypes.STANDARD_RAIL:
        default:
            return (
                <Tile
                    tile={tile as ITile}
                    index={index}
                    slidesPerView={slidesPerView}
                    key={index}
                    tiles={tiles}
                    disableAnimation={disableAnimation}
                />
            );
    }
};

export default Rail;
